/**
 * Versioned contracts for the US Data Center Community & Economic Impact
 * capability. Unknown values are always absent/null; zero is never a sentinel.
 */

import type { BopHeatRejection, CoolingGeneration, ProjectIntent } from './siteRag';
import type { AdiabaticWaterScreeningResult } from '../lib/economicContribution/adiabaticWaterScreening';

export const ECONOMIC_CONTRIBUTION_SCHEMA_VERSION = 1 as const;
export const DIRECT_JOBS_MODEL_VERSION = 'direct-jobs-screening-v1' as const;
export const DIRECT_JOBS_MW_RANGE_MODEL_VERSION = 'direct-jobs-mw-range-screening-v1' as const;
export const CONSTRUCTION_JOBS_MODEL_VERSION = 'construction-jobs-screening-v1' as const;
export const CONSTRUCTION_JOBS_MW_RANGE_MODEL_VERSION = 'construction-jobs-mw-range-screening-v1' as const;
export const FISCAL_SCENARIO_MODEL_VERSION = 'manual-fiscal-scenario-v1' as const;
export const WATER_USE_SCREENING_MODEL_VERSION = 'water-use-screening-v8' as const;

export type EconomicEvidenceBasis =
  | 'verified-actual'
  | 'observed-public-record'
  | 'committed'
  | 'announced'
  | 'secondary-discovery'
  | 'modelled'
  | 'operator-provided';

export type EconomicCatalogLifecycle =
  | 'proposed'
  | 'approved-current'
  | 'approved-stale'
  | 'retired'
  | 'rejected';

export type DataCenterOperatingModel =
  | 'hyperscale'
  | 'colocation'
  | 'enterprise'
  | 'shell-lease'
  | 'unknown';

export type DataCenterWorkload =
  | 'general-cloud'
  | 'ai-hpc'
  | 'inference'
  | 'mixed'
  | 'unknown';

export type PowerMwBasis = 'it' | 'facility' | 'utility-service';

export type PowerMwBasisTreatment =
  | 'as-recorded'
  | 'facility-as-utility-service-screening-proxy'
  | 'unspecified-as-facility-as-utility-service-screening-proxy'
  | 'it-pue-to-facility-as-utility-service-screening-proxy';

export type ScreeningInputSource = 'site-record' | 'screening-assumption';

/** Comparable-only basis. Backup plant nameplate must never be treated as site demand. */
export type ComparablePowerMwBasis = PowerMwBasis | 'backup-generation';

/**
 * Authority describes who published an observation. It is deliberately
 * independent of both evidence basis (what the number represents) and record
 * lifecycle (whether the record has passed promotion policy).
 */
export type ComparableEvidenceAuthority =
  | 'regulatory-filing'
  | 'government-record'
  | 'utility-or-iso-record'
  | 'operator-primary'
  | 'professional-analysis'
  | 'secondary-reporting'
  | 'discovery-only';

export type ComparableWaterRecordStage =
  | 'design-estimate'
  | 'application-request'
  | 'permit-limit'
  | 'reported-actual';

export type ComparableWaterFlow = 'withdrawal' | 'consumption' | 'discharge' | 'unspecified';

export type ComparableWaterUnit =
  | 'gal/day'
  | 'million-gal/day'
  | 'acre-ft/year'
  | 'liters/day'
  | 'gallons'
  | 'million-gallons'
  | 'acre-ft'
  | 'liters';

/** @deprecated Use ComparableWaterUnit; retained for early contract consumers. */
export type ComparableWaterRateUnit = ComparableWaterUnit;

/** A stage-specific water fact. Requested, permitted and actual use never overwrite one another. */
export interface ComparableWaterObservationValue {
  kind: 'water';
  stage: ComparableWaterRecordStage;
  flow: ComparableWaterFlow;
  amount: number;
  units: ComparableWaterUnit;
  /** Canonical comparison value; the native amount and units remain authoritative. */
  normalizedGallonsPerDay?: number;
  statistic:
    | 'design-peak-day'
    | 'maximum-day'
    | 'average-day'
    | 'monthly-total'
    | 'annual-total'
    | 'reporting-period-total'
    | 'unspecified';
  boundary: 'campus' | 'building' | 'phase' | 'utility-account' | 'unknown';
  waterSource?: 'municipal' | 'groundwater' | 'surface' | 'reclaimed' | 'mixed' | 'unknown';
  permitOrApplicationId?: string;
  periodStart?: string;
  periodEnd?: string;
}

export type ComparableRackCoolingStrategy = ProjectIntent['coolingStrategy'] | 'unknown';
export type ComparableHeatRejection = BopHeatRejection;
export type ComparableCoolingGeneration = CoolingGeneration;

/** Cooling is represented on three independent axes; absent axes remain unknown. */
export interface ComparableCoolingObservationValue {
  kind: 'cooling';
  rackCoolingStrategy?: ComparableRackCoolingStrategy;
  heatRejection?: ComparableHeatRejection;
  coolingGeneration?: ComparableCoolingGeneration;
  scope: 'campus' | 'building' | 'phase' | 'unknown';
  /** Original public-record wording when it does not map cleanly to a canonical axis. */
  nativeDescription?: string;
}

export type ComparablePowerTechnology =
  | 'grid-service'
  | 'onsite-natural-gas'
  | 'onsite-diesel'
  | 'onsite-solar'
  | 'onsite-wind'
  | 'battery-storage'
  | 'fuel-cell'
  | 'small-modular-nuclear'
  | 'large-nuclear'
  | 'renewable-energy-contract'
  | 'unknown';

export interface ComparablePowerObservationValue {
  kind: 'power';
  role: 'load-service' | 'primary-generation' | 'backup-generation' | 'energy-contract';
  technologies: readonly ComparablePowerTechnology[];
  status: 'requested' | 'studied' | 'contracted' | 'energized' | 'operating' | 'retired' | 'unknown';
  capacityMw?: number;
  capacityBasis?: ComparablePowerMwBasis;
  utility?: string;
  isoRto?: string;
  docketOrAgreementId?: string;
}

export type ComparableDeliveryEventCategory =
  | 'power'
  | 'water'
  | 'permitting'
  | 'zoning-land-use'
  | 'community'
  | 'construction'
  | 'financing'
  | 'operations';

/** Factual event taxonomy. "Success" and "obstacle" are presentation inferences, not stored claims. */
export interface ComparableDeliveryEventValue {
  kind: 'delivery-event';
  category: ComparableDeliveryEventCategory;
  eventType:
    | 'application-filed'
    | 'permit-issued'
    | 'permit-denied'
    | 'approval-granted'
    | 'approval-denied'
    | 'agreement-executed'
    | 'service-energized'
    | 'construction-started'
    | 'construction-delayed'
    | 'operations-started'
    | 'capacity-reduced'
    | 'project-cancelled'
    | 'legal-challenge-filed'
    | 'legal-challenge-resolved'
    | 'public-opposition-recorded'
    | 'public-support-recorded'
    | 'milestone-other';
  occurredOn: string;
  summary: string;
  effect: 'advanced' | 'delayed' | 'constrained' | 'stopped' | 'neutral' | 'unknown';
  linkedEventIds?: readonly string[];
  scheduleImpactMonths?: number;
  capacityImpactMw?: number;
}

export interface EconomicSourceRef {
  id: string;
  title: string;
  publisher: string;
  url: string;
  publicationDate?: string;
  accessedAt: string;
  /** Exact short quote or faithful table transcription stored for rapid verification. */
  excerpt: string;
  /** Page, table, section, docket item, or line range containing the excerpt. */
  locator: string;
  excerptKind: 'verbatim-quote' | 'table-transcription';
  /** Whether the source supplies the coefficient or independently checks its scale/scope. */
  evidenceRole: 'numeric-basis' | 'independent-cross-check';
  normalizedChecks?: readonly {
    use:
      | 'operations-fte-per-mw'
      | 'construction-peak-workers-per-mw'
      | 'construction-average-to-peak-ratio'
      | 'construction-duration-years';
    mw?: number;
    low: number;
    high: number;
    units: 'workers-per-utility-service-mw' | 'ratio' | 'years';
  }[];
  page?: string;
  notes?: string;
}

export interface EconomicCoefficientBand {
  id: string;
  label: string;
  minMwInclusive: number;
  maxMwExclusive: number | null;
  low: number;
  high: number;
}

export type EconomicCoefficient =
  | {
      kind: 'operations-coverage-floor';
      onsitePositionsLow: number;
      onsitePositionsHigh: number;
      weeklyCoverageHours: number;
      annualHoursPerFte: number;
    }
  | {
      kind: 'size-band-intensity';
      use: 'operations-fte-per-mw' | 'construction-peak-workers-per-mw';
      mwBasis: 'utility-service';
      bands: readonly EconomicCoefficientBand[];
    }
  | {
      kind: 'construction-average-to-peak-ratio';
      low: number;
      high: number;
    }
  | {
      kind: 'construction-duration-years';
      low: number;
      high: number;
    };

export interface EconomicSourceCatalogEntry {
  id: string;
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  lifecycle: EconomicCatalogLifecycle;
  coefficient: EconomicCoefficient;
  sources: readonly EconomicSourceRef[];
  publicationVintage: string;
  geographicScope: 'US' | 'global' | string;
  facilityScope: string;
  nativeUnits: string;
  nativeDenominator?: string;
  jobDefinition: string;
  timeBasis: string;
  transformation: string;
  permittedUses: readonly string[];
  proposedBy: string;
  proposedAt: string;
  /** Named review remains supported, but is not required when policy validation is present. */
  reviewedBy?: string;
  reviewedAt?: string;
  validationPolicyId?: string;
  validatedAt?: string;
  nextReviewAt?: string;
  supersededBy?: string;
  rejectionReason?: string;
  limitations: readonly string[];
}

export interface EconomicSourceCatalogSnapshot {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  catalogVersion: string;
  createdAt: string;
  entries: readonly EconomicSourceCatalogEntry[];
}

export interface MwQuantity {
  value: number;
  /** Published margin of error where the source supplies one (for example ACS). */
  marginOfError?: number;
  basis: PowerMwBasis;
  phaseBasis: string;
  /** Original site basis when a disclosed staffing-model proxy is applied. */
  sourceBasis?: PowerMwBasis;
  /** Explicit conversion/proxy treatment; absence preserves legacy snapshots. */
  basisTreatment?: PowerMwBasisTreatment;
  /** Whether the original MW basis was recorded or supplied as a disclosed default. */
  basisSource?: ScreeningInputSource;
  /** PUE used when an IT-load value is explicitly converted for a staffing screen. */
  basisConversionPue?: WaterUseRangeAssumption;
}

export interface ComparableMwQuantity extends Omit<MwQuantity, 'basis'> {
  basis: ComparablePowerMwBasis;
}

/** A bounded site-intent range. Endpoints remain explicit; no midpoint is presented as a site fact. */
export interface MwRangeQuantity {
  low: number;
  high: number;
  basis: PowerMwBasis;
  phaseBasis: string;
  /** Original basis when a documented screening proxy is required. */
  sourceBasis?: PowerMwBasis;
  basisTreatment: PowerMwBasisTreatment;
  /** Whether the original MW basis was recorded or supplied as a disclosed default. */
  basisSource?: ScreeningInputSource;
  /** PUE used when IT-load endpoints are explicitly converted for a staffing screen. */
  basisConversionPue?: WaterUseRangeAssumption;
}

export type WaterUseFlowBasis =
  | 'withdrawal'
  | 'consumption'
  | 'cooling-water'
  | 'unspecified';

export type WaterUseScenarioKind =
  | 'modern-operator-fleet'
  | 'national-current-average'
  | 'wet-cooling-tower-consumption'
  | 'wet-cooling-tower-withdrawal'
  | 'dry-cooling';

export interface WaterUseRangeAssumption {
  low: number;
  high: number;
  source: 'site-record' | 'screening-assumption';
  sourceLabel?: string;
}

/**
 * Trusted local weather context projected from the site's exact current SED.
 * These values qualify cooling-system suitability and result interpretation;
 * they do not create an hourly economizer or hybrid wet-mode schedule.
 */
export interface WaterUseAmbientConditions {
  averageDryBulbC: number;
  peakDryBulbC: number;
  averageWetBulbC: number;
  /**
   * Selected SCREENING wet bulb (legacy field name): the NOAA normals estimate
   * (monthly mean temperature at an assumed RH) or the SED highest-monthly
   * value. Never an ASHRAE design percentile or equipment duty.
   */
  designWetBulbC: number;
  /** Persisted identifiers; `'noaa-design'` is legacy for the NOAA-normals estimate. */
  wetBulbBasis: 'noaa-design' | 'sed-peak-proxy';
  relativeHumidityPct: number;
  monthlyClimatePoints: number;
  source: 'site-record';
  sourceLabel: string;
  sedId: string;
  sedVersion: number;
  dataAsOf?: string;
  weatherStationLabel?: string;
}

/**
 * Optional water-specific context. Rack cooling and workload remain
 * explanatory only: neither is allowed to select the cold-end heat-rejection
 * scenario without an explicit `heatRejection` value.
 */
export interface WaterUseInputContext {
  pue?: WaterUseRangeAssumption;
  /** Annual-average fraction of nameplate IT/facility load, expressed from 0 to 1. */
  utilization?: WaterUseRangeAssumption;
  heatRejection?: BopHeatRejection;
  /** Cooling-tower cycles of concentration; values must be greater than one. */
  cyclesOfConcentration?: WaterUseRangeAssumption;
  rackCoolingStrategy?: ProjectIntent['coolingStrategy'];
  workload?: DataCenterWorkload;
  operatingModel?: DataCenterOperatingModel;
  /** Exact-current SED climate; refreshed from the site record, never hand-copied into Intent. */
  ambientConditions?: WaterUseAmbientConditions;
}

export interface WaterUseScreeningInput {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  siteId: string;
  /** Canonical site coordinates used to select/cache the hourly TMY. */
  siteCoordinates?: {
    latitude: number;
    longitude: number;
  };
  loadRange: MwRangeQuantity;
  context?: WaterUseInputContext;
}

export interface WaterUseBenchmarkSourceRef extends EconomicSourceRef {
  publisherClass: 'federal-laboratory' | 'federal-agency' | 'public-research-body' | 'operator-primary' | 'standards-body';
}

export interface WaterUseWueBenchmark {
  id: string;
  label: string;
  scenario: Extract<WaterUseScenarioKind, 'modern-operator-fleet' | 'national-current-average'>;
  lowLitersPerKwhIt: number;
  highLitersPerKwhIt: number;
  flowBasis: WaterUseFlowBasis;
  sourceRefIds: readonly string[];
  limitations: readonly string[];
}

export interface WaterUseWetTowerBenchmark {
  id: string;
  /**
   * ASHRAE nominal cooling-tower ton heat-rejection basis. This is 15,000
   * Btu/h, not the 12,000 Btu/h evaporator/refrigeration-ton basis.
   */
  nominalCoolingTowerTonBtuPerHour: number;
  evaporationGallonsPerTonHour: number;
  makeupGallonsPerTonHour: { low: number; high: number };
  defaultCyclesOfConcentration: { low: number; high: number };
  sourceRefIds: readonly string[];
  limitations: readonly string[];
}

export type WaterUseFacilityWaterClass = 'W32' | 'W40' | 'W45';

export type WaterUseDryCoolingCapacityStatus =
  | 'dry-only-screening-candidate'
  | 'vendor-selection-required'
  | 'assist-or-trim-required'
  | 'reference-temperature-exceeded';

export interface WaterUseDryCoolingClassCheck {
  waterClass: WaterUseFacilityWaterClass;
  maxFacilitySupplyC: number;
  dryStatus: WaterUseDryCoolingCapacityStatus;
  /** Facility-water temperature required at the site design dry bulb. */
  dryRequiredFacilitySupplyC: { low: number; high: number };
  adiabaticStatus: WaterUseDryCoolingCapacityStatus;
  /** Facility-water temperature required after generic adiabatic pre-cooling. */
  adiabaticRequiredFacilitySupplyC: { low: number; high: number };
}

/**
 * Screening-only capacity check using published current design ranges. It is
 * deliberately separate from a manufacturer selection or annual operating
 * schedule and therefore cannot by itself establish a project water demand.
 */
export interface WaterUseDryCoolingDesignScreen {
  basis: 'published-engineering-range-screening';
  /** Current SED peak temperature used conservatively; not an ASHRAE design percentile. */
  sitePeakDryBulbProxyC: number;
  siteWetBulbC: number;
  /** `'tmy-coincident'`: wet bulb coincident with the hottest validated TMY hours; otherwise the SED screening basis. */
  wetBulbBasis: WaterUseAmbientConditions['wetBulbBasis'] | 'tmy-coincident';
  /** Full declared facility heat duty; disclosed but not converted to fake OEM capacity. */
  screeningDutyMw: { low: number; high: number };
  /** Air-to-liquid dry-cooler approach only. */
  dryCoolerApproachC: { low: number; high: number };
  /** Reference CDU approach in series between the dry cooler and facility interface. */
  cduApproachC: number;
  /** Optional additional plate-and-frame exchanger allowance. */
  additionalPlateHeatExchangerApproachC: { low: number; high: number };
  /** CDU plus optional additional plate-and-frame exchanger. */
  seriesHeatExchangerApproachC: { low: number; high: number };
  /** Total ambient-to-facility-supply approach used for the class feasibility result. */
  totalAmbientToFacilitySupplyApproachC: { low: number; high: number };
  adiabaticSaturationEffectiveness: { low: number; high: number };
  adiabaticCoilInletC: { low: number; high: number };
  classChecks: readonly WaterUseDryCoolingClassCheck[];
  sourceRefIds: readonly string[];
  limitations: readonly string[];
}

export interface WaterUseDryCoolingScreeningBenchmark {
  waterClasses: readonly {
    id: WaterUseFacilityWaterClass;
    maxFacilitySupplyC: number;
  }[];
  dryCoolerApproachC: { low: number; high: number };
  cduApproachC: number;
  additionalPlateHeatExchangerApproachC: { low: number; high: number };
  adiabaticSaturationEffectiveness: { low: number; high: number };
  sourceRefIds: readonly string[];
  limitations: readonly string[];
}

export interface WaterUseBenchmarkCatalogSnapshot {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  catalogVersion: string;
  createdAt: string;
  sources: readonly WaterUseBenchmarkSourceRef[];
  defaults: {
    pue: WaterUseRangeAssumption & { sourceRefIds: readonly string[] };
    utilization: WaterUseRangeAssumption & { sourceRefIds: readonly string[] };
  };
  wueBenchmarks: readonly WaterUseWueBenchmark[];
  wetTower: WaterUseWetTowerBenchmark;
  dryCoolingScreening: WaterUseDryCoolingScreeningBenchmark;
}

export interface WaterUseScenarioResult {
  scenarioId: string;
  kind: WaterUseScenarioKind;
  label: string;
  estimateBasis: 'modelled-screening';
  applicability: 'benchmark' | 'selected-technology' | 'sensitivity';
  flowBasis: WaterUseFlowBasis;
  boundary: 'onsite-direct-water' | 'cooling-process-only';
  /** Present when a numerical endpoint is conditional on an unverified equipment/design capability. */
  designQualification?: 'dry-capacity-unverified';
  /** Typical-range capacity screen; never a substitute for an OEM curve. */
  dryCoolingDesignScreen?: WaterUseDryCoolingDesignScreen;
  /**
   * Site-coordinate-selected, nearest-grid 8,760-hour TMY screening of recirculating adiabatic-assist
   * makeup. This estimates the water consequence of the stated assumptions;
   * it does not certify that the selected dry equipment can carry full duty.
   */
  adiabaticWaterScreening?: AdiabaticWaterScreeningResult;
  annualGallons: { low: number; high: number; unit: 'gallons/year' };
  averageGallonsPerDay: { low: number; high: number; unit: 'gal/day' };
  /** Technology sensitivity at 100% load; not a design-day or permit quantity. */
  fullLoadGallonsPerDay?: { low: number; high: number; unit: 'gal/day' };
  coefficient: {
    low: number;
    high: number;
    unit: 'liters-per-kwh-it' | 'gallons-per-ton-hour';
  };
  sourceRefs: readonly WaterUseBenchmarkSourceRef[];
  assumptions: readonly string[];
  limitations: readonly string[];
  trace: readonly EconomicCalculationStep[];
}

export interface WaterUseScreeningResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof WATER_USE_SCREENING_MODEL_VERSION;
  status: 'calculated' | 'not-calculated';
  siteId: string;
  estimateBasis: 'modelled-screening';
  loadRange: MwRangeQuantity;
  resolvedContext: {
    pue: WaterUseRangeAssumption;
    utilization: WaterUseRangeAssumption;
    heatRejection: BopHeatRejection;
    cyclesOfConcentration?: WaterUseRangeAssumption;
    /** Electrical/thermal ranges actually used by the water equations. */
    itLoadMw: { low: number; high: number };
    facilityHeatMw: { low: number; high: number };
    workload?: DataCenterWorkload;
    operatingModel?: DataCenterOperatingModel;
    rackCoolingStrategy?: ProjectIntent['coolingStrategy'];
    ambientConditions?: WaterUseAmbientConditions;
    /** Climate may support the published-range design screen but never an invented hourly schedule. */
    climateTreatment:
      | 'tmy-hourly-adiabatic-screening'
      | 'site-context-design-screen-no-hourly-adjustment'
      | 'site-context-no-hourly-adjustment'
      | 'not-available';
  } | null;
  scenarios: readonly WaterUseScenarioResult[];
  /** Whether this run could add the site-coordinate-selected hourly adiabatic estimate. */
  adiabaticClimateStatus?: {
    status: 'calculated' | 'unavailable' | 'not-requested';
    reason?: 'tmy-unavailable' | 'invalid-tmy';
  };
  unavailableReason?: 'invalid-input' | 'invalid-assumption' | 'benchmark-catalog-invalid';
  modelInputHash: string;
  sourceCatalogVersion: string;
  sourceRefIds: readonly string[];
  limitations: readonly string[];
}

export interface DirectJobsScreeningInput {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  siteId: string;
  location: { stateFips: string; countyFips: string; stateCode?: string; countyName?: string };
  load: MwQuantity;
  operatingModel: DataCenterOperatingModel;
  workload: DataCenterWorkload;
  buildingCount?: number;
  rackCount?: number;
  operatorProvided?: {
    permanentOnsiteFte: number;
    sourceLabel: string;
    asOf?: string;
  };
}

export interface EconomicCalculationStep {
  id: string;
  expression: string;
  inputs: Record<string, number | string | null>;
  output: number | string | null;
  sourceEntryIds: readonly string[];
}

export interface DirectJobsScreeningResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof DIRECT_JOBS_MODEL_VERSION;
  status: 'calculated' | 'operator-provided' | 'not-calculated';
  siteId: string;
  range: { low: number; high: number; unit: 'direct-onsite-fte' } | null;
  evidenceBasis: 'modelled' | 'operator-provided' | null;
  method: 'coverage-floor-plus-size-band' | 'operator-provided' | null;
  facilityOperationsRange: { low: number; high: number } | null;
  itAndSiteOperationsRange: { low: number; high: number } | null;
  contractedServicesRange: { low: number; high: number } | null;
  factors: readonly string[];
  limitations: readonly string[];
  unavailableReason?:
    | 'invalid-input'
    | 'unsupported-mw-basis'
    | 'outside-approved-domain'
    | 'approved-source-unavailable';
  modelInputHash: string;
  sourceCatalogVersion: string;
  sourceEntryIds: readonly string[];
  trace: readonly EconomicCalculationStep[];
}

export interface DirectJobsMwRangeScreeningResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof DIRECT_JOBS_MW_RANGE_MODEL_VERSION;
  status: 'calculated' | 'partially-calculated' | 'not-calculated';
  siteId: string;
  loadRange: MwRangeQuantity;
  lowEndpoint: DirectJobsScreeningResult;
  highEndpoint: DirectJobsScreeningResult;
  sourceEntryIds: readonly string[];
  limitations: readonly string[];
  modelInputHash: string;
  sourceCatalogVersion: string;
}

export interface ConstructionJobsInput {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  siteId: string;
  load: MwQuantity;
  constructionDurationMonths?: number;
  capitalExpenditureUsd?: number;
  operatorProvided?: {
    peakOnsiteWorkers?: number;
    averageOnsiteWorkers?: number;
    directJobYears?: number;
    durationMonths?: number;
    sourceLabel: string;
  };
}

export interface ConstructionJobsResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof CONSTRUCTION_JOBS_MODEL_VERSION;
  status: 'calculated' | 'operator-provided' | 'not-calculated';
  siteId: string;
  peakOnsiteWorkers: { low: number; high: number } | null;
  averageOnsiteWorkers: { low: number; high: number } | null;
  directJobYears: { low: number; high: number } | null;
  durationMonths: number | { low: number; high: number } | null;
  evidenceBasis: 'modelled' | 'operator-provided' | null;
  phaseBasis: string;
  unavailableReason?:
    | 'invalid-input'
    | 'unsupported-mw-basis'
    | 'outside-approved-domain'
    | 'approved-source-unavailable'
    | 'operator-input-incomplete';
  modelInputHash: string;
  sourceCatalogVersion: string;
  sourceEntryIds: readonly string[];
  trace: readonly EconomicCalculationStep[];
  limitations: readonly string[];
}

export interface ConstructionJobsMwRangeScreeningResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof CONSTRUCTION_JOBS_MW_RANGE_MODEL_VERSION;
  status: 'calculated' | 'partially-calculated' | 'not-calculated';
  siteId: string;
  loadRange: MwRangeQuantity;
  lowEndpoint: ConstructionJobsResult;
  highEndpoint: ConstructionJobsResult;
  sourceEntryIds: readonly string[];
  limitations: readonly string[];
  modelInputHash: string;
  sourceCatalogVersion: string;
}

export interface EconomicMetricObservation {
  metric:
    | 'establishments'
    | 'employment'
    | 'annual-average-wage'
    | 'median-hourly-wage'
    | 'labor-force'
    | 'unemployment-rate'
    | 'population'
    | 'median-household-income'
    | 'gross-domestic-product'
    | 'personal-income';
  value: number;
  units: string;
  geography: { kind: 'county' | 'metro' | 'nonmetro' | 'state' | 'national'; code: string; label: string };
  industry?: { code: string; label: string };
  occupation?: { code: string; label: string };
  period: string;
  sourceDataset: 'QCEW' | 'OEWS' | 'LAUS' | 'CBP' | 'ACS' | 'BEA' | 'QWI';
  sourceUrl: string;
  suppressed: false;
  fallbackLevel: 1 | 2 | 3 | 4 | 5;
}

export interface EconomicProfileSnapshot {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  snapshotId: string;
  countyFips: string;
  stateFips: string;
  stateCode: string;
  countyName: string;
  generatedAt: string;
  observations: readonly EconomicMetricObservation[];
  missingMetrics: readonly string[];
  /** Provider failures are distinct from legitimately unpublished/suppressed cells. */
  providerErrors: readonly string[];
  sourceVintages: Readonly<Record<string, string>>;
}

/** Minimal client request; canonical coordinates and FIPS are resolved server-side. */
export interface EconomicWorkforceProfileRequest {
  siteId: string;
  ownerUid?: string;
}

export interface EconomicWorkforceProfileResponse {
  profile: EconomicProfileSnapshot;
  /** True when the server retained an already-current or concurrent snapshot. */
  unchanged: boolean;
}

export interface ComparableObservation<T> {
  /** Stable within the project record; required by policy for structured observations. */
  observationId?: string;
  value: T;
  evidenceBasis: Exclude<EconomicEvidenceBasis, 'modelled' | 'operator-provided'>;
  /** Optional only for legacy scalar observations; structured observations require it. */
  evidenceAuthority?: ComparableEvidenceAuthority;
  sources: readonly EconomicSourceRef[];
  asOf?: string;
  phaseBasis: string;
  limitations: readonly string[];
}

export interface ComparableProjectRecord {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  projectId: string;
  facilityName: string;
  operator?: string;
  projectEntity?: string;
  location: { stateFips: string; countyFips: string; stateCode: string; locality?: string };
  status: 'proposed' | 'construction' | 'operating' | 'cancelled' | 'unknown';
  operatingModel: DataCenterOperatingModel;
  workload: DataCenterWorkload;
  mw?: ComparableObservation<ComparableMwQuantity>;
  rackCount?: ComparableObservation<number>;
  buildingAreaSqFt?: ComparableObservation<number>;
  capitalInvestmentUsd?: ComparableObservation<number>;
  capitalInvestmentUsdRange?: ComparableObservation<{ low: number; high: number }>;
  constructionPeakWorkers?: ComparableObservation<number>;
  constructionPeakWorkersRange?: ComparableObservation<{ low: number; high: number }>;
  constructionJobYears?: ComparableObservation<number>;
  permanentJobs?: ComparableObservation<number>;
  permanentJobsRange?: ComparableObservation<{ low: number; high: number }>;
  annualPayrollUsd?: ComparableObservation<number>;
  averageWageUsd?: ComparableObservation<number>;
  assessedValueUsd?: ComparableObservation<number>;
  propertyTaxPaidUsd?: ComparableObservation<number>;
  pilotPaymentUsd?: ComparableObservation<number>;
  /** @deprecated Legacy scalar retained for stored-record compatibility. */
  water?: ComparableObservation<{ value: number; units: string; basis: 'withdrawal' | 'consumption' | 'authorized' }>;
  /** Stage-specific observations. Conflicting claims remain separate array entries. */
  waterTimeline?: readonly ComparableObservation<ComparableWaterObservationValue>[];
  coolingObservations?: readonly ComparableObservation<ComparableCoolingObservationValue>[];
  powerObservations?: readonly ComparableObservation<ComparablePowerObservationValue>[];
  deliveryEvents?: readonly ComparableObservation<ComparableDeliveryEventValue>[];
  reviewStatus: 'proposed' | 'approved' | 'rejected';
  proposedBy?: string;
  proposedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  validationPolicyId?: string;
  validatedAt?: string;
  sourceRecordHash?: string;
  confidence: 'high' | 'medium' | 'low';
  limitations: readonly string[];
}

export interface ComparableMatchResult {
  record: ComparableProjectRecord;
  score: number;
  reasons: readonly string[];
  incompatibilities: readonly string[];
  compatibleForJobsPerMw: boolean;
}

export interface ComparableAggregate {
  evidenceBasis: 'verified-actual' | 'observed-public-record' | 'committed' | 'announced';
  compatibleRecordCount: number;
  permanentJobs: { low: number; median: number; high: number } | null;
  jobsPerMw: { low: number; median: number; high: number } | null;
  method: 'compatible-observation-envelope' | null;
}

export interface FiscalScenarioInput {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  scenarioId: string;
  label: string;
  jurisdictionLabel: string;
  currency: 'USD';
  currencyYear: number;
  startYear: number;
  years: number;
  assessedValueUsd: number;
  annualTaxRateDecimal: number;
  abatementPctByYear?: readonly number[];
  pilotUsdByYear?: readonly number[];
  pilotTreatment?: 'replacement' | 'additional';
}

export interface FiscalScenarioYear {
  year: number;
  grossTaxUsd: number;
  abatementPct: number;
  taxAfterAbatementUsd: number;
  pilotUsd: number;
  communityRevenueUsd: number;
  cumulativeCommunityRevenueUsd: number;
}

export interface FiscalScenarioResult {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  modelVersion: typeof FISCAL_SCENARIO_MODEL_VERSION;
  status: 'illustrative-screening-scenario';
  scenarioId: string;
  jurisdictionLabel: string;
  currency: 'USD';
  currencyYear: number;
  years: readonly FiscalScenarioYear[];
  cumulativeCommunityRevenueUsd: number;
  inputHash: string;
  limitations: readonly string[];
}

export interface EconomicContributionFinding {
  id: string;
  label: string;
  value?: number;
  range?: { low: number; high: number };
  units: string;
  evidenceBasis: EconomicEvidenceBasis | 'unavailable';
  method?: string;
  confidence: 'high' | 'medium' | 'low' | 'unavailable';
  sourceRefs: readonly EconomicSourceRef[];
  modelVersion?: string;
  inputHash?: string;
  limitations: readonly string[];
}

export interface ExternalEconomicImpactStudy {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  studyId: string;
  siteId: string;
  consultant: string;
  model: 'IMPLAN' | 'RIMS-II' | 'other';
  modelVersion?: string;
  regionDefinition: string;
  industryDefinition: string;
  modelYear: number;
  sourceDocumentId: string;
  directInputs: Readonly<Record<string, number | string>>;
  reportedResults: {
    directJobs?: number;
    indirectJobs?: number;
    inducedJobs?: number;
    laborIncomeUsd?: number;
    economicOutputUsd?: number;
    taxRevenueUsd?: number;
  };
  validatedBy: string;
  validatedAt: string;
  limitations: readonly string[];
}

/** Minimal additive site inputs; existing project intent remains the first source. */
export interface EconomicContributionSiteInputs {
  loadMw?: number;
  /** Explicit bounded refinement, used for a selected Intent phase with a closed MW range. */
  loadRange?: { low: number; high: number };
  loadBasis?: PowerMwBasis;
  phaseBasis?: string;
  operatingModel?: DataCenterOperatingModel;
  workload?: DataCenterWorkload;
  constructionDurationMonths?: number;
  operatorPermanentOnsiteFte?: number;
  operatorPermanentJobsSource?: string;
  operatorConstruction?: ConstructionJobsInput['operatorProvided'];
  /** Optional refinements for the deterministic water-use screening scenarios. */
  waterUse?: WaterUseInputContext;
  /** Explicit user scenarios only; no jurisdiction eligibility is inferred. */
  fiscalScenarios?: readonly FiscalScenarioInput[];
}

/** A selectable project/phase scenario derived from canonical Project Intent. */
export interface EconomicScreeningScenarioOption {
  id: string;
  label: string;
  phaseBasis: string;
  loadMw?: number;
  loadRange?: { low: number; high: number };
  loadBasis: PowerMwBasis;
  loadBasisSource?: ScreeningInputSource;
  operatingModel: DataCenterOperatingModel;
  workload: DataCenterWorkload;
  constructionDurationMonths?: number;
  waterUse?: WaterUseInputContext;
  disabledReason?: string;
}

/** Effective, source-aware form defaults; these are not persisted as refinements unless edited. */
export interface EconomicScreeningSiteDefaults {
  loadMw?: number;
  loadRange?: { low: number; high: number };
  loadBasis: PowerMwBasis;
  loadBasisSource?: ScreeningInputSource;
  phaseBasis: string;
  operatingModel: DataCenterOperatingModel;
  workload: DataCenterWorkload;
  constructionDurationMonths?: number;
  waterUse?: WaterUseInputContext;
  jurisdictionLabel?: string;
  scenarioOptions: readonly EconomicScreeningScenarioOption[];
}

export interface EconomicContributionScreenInput {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  directJobs: DirectJobsScreeningInput;
  /** Present when directJobs.load is the low endpoint of a bounded site-intent range. */
  directJobsLoadRange?: MwRangeQuantity;
  constructionJobs?: ConstructionJobsInput;
  waterUse?: WaterUseScreeningInput;
  fiscalScenarios?: readonly FiscalScenarioInput[];
}

/** Additional conditional cases use the same source catalog and calculation as the primary screen. */
export interface EconomicContributionScenarioInput extends EconomicContributionScreenInput {
  scenarioId: string;
  label: string;
  assumption: string;
}

export interface EconomicContributionScreenRequest extends EconomicContributionScreenInput {
  additionalScenarios?: readonly EconomicContributionScenarioInput[];
}

export interface EconomicContributionScenarioScreen {
  scenarioId: string;
  label: string;
  assumption: string;
  directJobs: DirectJobsScreeningResult;
  directJobsMwRange: DirectJobsMwRangeScreeningResult | null;
  constructionJobs: ConstructionJobsResult | null;
  constructionJobsMwRange: ConstructionJobsMwRangeScreeningResult | null;
  waterUseScreening?: WaterUseScreeningResult | null;
}

export interface EconomicContributionScreeningSnapshot {
  schemaVersion: typeof ECONOMIC_CONTRIBUTION_SCHEMA_VERSION;
  snapshotId: string;
  siteId: string;
  location: { stateFips: string; countyFips: string; stateCode?: string; countyName?: string };
  generatedAt: string;
  inputHash: string;
  sourceCatalogVersion: string;
  directJobs: DirectJobsScreeningResult;
  directJobsMwRange: DirectJobsMwRangeScreeningResult | null;
  constructionJobs: ConstructionJobsResult | null;
  constructionJobsMwRange: ConstructionJobsMwRangeScreeningResult | null;
  waterUseScreening?: WaterUseScreeningResult | null;
  economicProfile: EconomicProfileSnapshot | null;
  comparableMatches: readonly ComparableMatchResult[];
  comparableAggregates: readonly ComparableAggregate[];
  fiscalScenarios: readonly FiscalScenarioResult[];
  additionalScenarios?: readonly EconomicContributionScenarioScreen[];
}
